import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { withErrorHandling } from '@selfserve/react-hocs';
import { CommonLayout, Breadcrumb } from '@selfserve/react-ui';
import {
  useStorage,
  storage,
  storageKeys,
  useCardFormik,
  FormValues,
  useLog,
} from '@selfserve/react-hooks';
import {
  apiStatuses,
  Chargebee,
  routesPaths,
  handleNoDataOnResponse,
  billingCycles,
  billingCycleType,
  billingCyclePayload,
  responseTypes,
  buttonIds,
} from '@selfserve/react-utils';
import {
  PlanItemInfo,
  useCreatePaymentIntent,
  useCreateCardWithIntent,
  useUpdateBillingAddress,
  useSubscriptionOverviewRequest,
} from '@selfserve/react-common-data';

import { planControllers } from '../constants';

import { ISummary, Summary } from './summary';

import {
  StyledPurchasePlanContainer,
  StyledPurchasePlanContentContainer,
  StyledFormContainer,
  StyledSummaryContainer,
  CheckoutForm,
} from '../ui';

import { PlanPayload, usePackageDetailRequest } from '../data';

export const Checkout = withErrorHandling(({ openErrorModal }) => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams();
  const { t } = useTranslation();
  const { log } = useLog();
  const [, setPlanId] = useStorage(storage.SESSION, storageKeys.PROMO_PLAN_ID);
  const [, setSubscriptionId] = useStorage(
    storage.SESSION,
    storageKeys.SUBSCRIPTION_ID
  );
  const [packageData] = useStorage(storage.SESSION, storageKeys.PACKAGE_DATA);

  const [checkoutText] = useStorage(
    storage.SESSION,
    storageKeys.SUMMARY_CHECKOUT_TEXT
  );
  const [, setCountryCode] = useStorage(
    storage.SESSION,
    storageKeys.COUNTRY_CODE
  );
  const [, setPromoCodesToApply] = useStorage<string[]>(
    storage.SESSION,
    storageKeys.PROMO_CODES_APPLY
  );
  const [, setPlanSettings] = useStorage(
    storage.SESSION,
    storageKeys.PLAN_SETTINGS
  );
  const [summary, setSummary] = useStorage(
    storage.SESSION,
    storageKeys.SUMMARY_PRICES
  );
  const [payload, setPayload] = useStorage(
    storage.SESSION,
    storageKeys.SUMMARY_PAYLOAD
  );
  const [amount, setAmount] = useState(0);
  const [waitingForConfig, setWaitingForConfig] = useState(!!subscriptionId);
  const [disabled, setDisabled] = useState(
    !subscriptionId && (!summary || !payload || false)
  );
  const { data: subscriptionOverviewData } = useSubscriptionOverviewRequest(
    !!subscriptionId
  );
  const { mutateAsync: createPaymentIntent } = useCreatePaymentIntent(amount);
  const { mutateAsync: createCardWithIntent } = useCreateCardWithIntent();
  const {
    data,
    isLoading,
    mutateAsync: updateBillingAddress,
  } = useUpdateBillingAddress();

  const handleAmountChange = (amount: number) =>
    setAmount(Math.round(amount * 100));

  const handleError = useCallback((text: string) => {
    setDisabled(true);
    openErrorModal({ text });
  }, []);

  const onSubmitAction = (
    values: FormValues,
    isSubmited: boolean,
    setIsSubmited: (value: boolean) => void,
    cardComponents: HTMLElement | undefined,
    handleApiError: (test: string) => void,
    t: TFunction
  ) => {
    if (!isSubmited) {
      const {
        address,
        city,
        country,
        firstName,
        lastName,
        state,
        zipCode,
        vatNumber,
      } = values;
      clearApiError();
      setIsSubmited(true);
      const cbInstance = Chargebee.getInstance();
      cbInstance
        .tokenize(cardComponents, {
          billingAddr1: address,
          billingCity: city,
          billingState: state ? t(`states.${country}.${state}`) : '',
          billingStateCode: state,
          billingZip: zipCode,
          billingCountry: t(`countries.${country}`),
          firstName,
          lastName,
        })
        .then(({ token: cbToken }: { token: string }) => {
          createPaymentIntent({})
            .then(({ status, data }) => {
              if (status.toString().startsWith(responseTypes.SUCCESS)) {
                cbInstance.load3DSHandler().then((threeDSHandler: any) => {
                  threeDSHandler.setPaymentIntent(data?.payment_intent);
                  threeDSHandler
                    .handleCardPayment(
                      { cbToken },
                      {
                        success: function (intent: any) {
                          createCardWithIntent({
                            billing_info: {
                              address: address,
                              city: city,
                              country_code: country,
                              state_code: state,
                              zip_code: zipCode,
                            },
                            intent_id: data?.payment_intent?.id,
                            gw_token:
                              intent.active_payment_attempt.id_at_gateway,
                            vat_number: vatNumber,
                          })
                            .then(({ status, data }) => {
                              if (
                                status
                                  .toString()
                                  .startsWith(responseTypes.SUCCESS)
                              ) {
                                const marketingInfoQuestions = packageData?.marketing_info;
                                if(marketingInfoQuestions && marketingInfoQuestions.length > 0) {
                                  navigate(
                                    routesPaths.MARKETING_INFO.replace(
                                      ':subscriptionId',
                                      data?._id
                                    ).replace(
                                      ':questionName',
                                      marketingInfoQuestions[0].name as string
                                    )
                                  );
                                } else {
                                  navigate(
                                    routesPaths.CONFIRMED.replace(
                                      ':subscriptionId',
                                      data?._id
                                    )
                                  );
                                }
                                setPromoCodesToApply([]);
                                setPlanSettings(null);
                                setCountryCode('');
                              } else {
                                const message = handleNoDataOnResponse(
                                  data?.message
                                );
                                handleApiError(
                                  message ||
                                    (data?.params && data?.params[0]) ||
                                    ''
                                );
                              }
                            })
                            .catch((err) => {
                              const error =
                                err?.displayMessage || err?.error?.message_en;
                              handleApiError(error || '');
                            });
                        },
                        error: function (intent: any, error: Error) {
                          handleApiError(error.message);
                        },
                      }
                    )
                    .catch((err: any) => log(err));
                });
              } else {
                const message = handleNoDataOnResponse(data?.message);
                handleApiError(message || '');
              }
            })
            .catch((err) => handleApiError(err.message || ''));
        })
        .catch((err: Error) => handleApiError(err.message || ''));
    }
  };

  const {
    formik,
    handleDropdownChange,
    handleCardInputsChange,
    handleCardInputsBlur,
    cardRef,
    apiError,
    isSubmited,
    clearApiError,
    handleApiError,
  } = useCardFormik({ onSubmitAction, openErrorModal });

  const { values, setFieldError, setFieldTouched, handleSubmit } = formik;

  const handleCountryChange = useCallback(() => {
    const { address, city, state, zipCode, country } = values;
    if (values.country) {
      updateBillingAddress({
        country_code: country,
        ...(state && { state_code: state }),
        ...(zipCode && { zip_code: zipCode }),
        ...(address && { address }),
        ...(city && { city }),
      });
    }
  }, [updateBillingAddress, values]);

  const handleZipCodeBlur = useCallback(() => {
    const { message_en } = data?.data || {};
    if (
      data?.status === apiStatuses.NOT_ACCEPTABLE &&
      message_en?.includes('zip')
    ) {
      handleCountryChange();
    }
  }, [data, values]);

  const getBillingCyclePayloadName = useCallback((billingCycle: string) => {
    switch (billingCycle) {
      default:
      case billingCycleType.YEAR:
        return billingCyclePayload[billingCycles.ANNUALLY];
      case billingCycleType.MONTH:
        return billingCyclePayload[billingCycles.MONTHLY];
    }
  }, []);

  const getAddonGroup = useCallback((abandonedPlan: PlanItemInfo) => {
    const { yearly_addons_info, monthly_addons_info } = abandonedPlan || {};
    switch (abandonedPlan.billing_cycle) {
      default:
      case billingCyclePayload[billingCycles.ANNUALLY]:
        return yearly_addons_info?.addon_group;
      case billingCyclePayload[billingCycles.MONTHLY]:
        return monthly_addons_info?.addon_group;
    }
  }, []);

  useEffect(() => {
    if (disabled) {
      openErrorModal({
        text: t('common.purchase-error'),
        url: routesPaths.LOGIN,
      });
    }
  }, [disabled]);

  useEffect(() => {
    handleCountryChange();
  }, [values.country]);

  useEffect(() => {
    if (data?.status === apiStatuses.NOT_ACCEPTABLE) {
      const { params } = data?.data || {};
      handleApiError(handleNoDataOnResponse(params));
    }
  }, [data]);

  useEffect(() => {
    const { status, data } = subscriptionOverviewData || {};
    if (status?.toString().startsWith(responseTypes.SUCCESS)) {
      const { subscriptionInfoList } = data || {};
      const abandonedPlan = subscriptionInfoList?.find(
        ({ id }) => id === subscriptionId
      );
      if (abandonedPlan) {
        setSubscriptionId(subscriptionId);
        setPlanId(subscriptionId);
        setSummary({
          plan: {
            ...abandonedPlan.plan,
            name: abandonedPlan.plan?.displayName,
            price: abandonedPlan.planItem?.pricing_info
              ?.filter(
                ({ billing_cycle }) =>
                  billing_cycle === billingCycleType.MONTH ||
                  billing_cycle === billingCycleType.YEAR
              )
              .find(
                ({ billing_cycle }) =>
                  abandonedPlan.billing_cycle ===
                  getBillingCyclePayloadName(billing_cycle as string)
              )?.price,
          },
          addons: abandonedPlan.addons?.map(
            ({ displayName, name, quantity }) => {
              const addonsGroup = getAddonGroup(abandonedPlan);
              const { group_info, group_items } =
                addonsGroup?.find(({ group_items }) =>
                  group_items?.find((a) => a.name === name)
                ) || {};
              const addonItem = group_items?.find((a) => a.name === name);
              const secondaryName = group_info?.controllerValues
                ? group_info?.controllerValues?.find(
                    (v) => v.value === quantity
                  )?.key
                : group_info?.controller === planControllers.COUNTER
                ? [quantity, addonItem?.headline].join(' ')
                : addonItem?.headline;
              return {
                name: displayName,
                key: name,
                quantity,
                secondaryName,
                price: abandonedPlan.addOnItems
                  ?.find(({ name: addonName }) => addonName === name)
                  ?.pricing_info?.find(
                    ({ billing_cycle }) =>
                      abandonedPlan.billing_cycle ===
                      getBillingCyclePayloadName(billing_cycle as string)
                  )?.price,
              };
            }
          ),
        });
        setPayload({
          billing_cycle: abandonedPlan.billing_cycle,
          partner_id: abandonedPlan.partner_id,
          currency: abandonedPlan.currency,
          product: abandonedPlan.product,
          addons: abandonedPlan.addons?.map(({ name, quantity }) => ({
            quantity,
            name,
          })),
          plan: {
            quantity: abandonedPlan.plan?.quantity,
            name: abandonedPlan.plan?.name,
          },
        });
      } else {
        setDisabled(true);
      }
    } else if (status) {
      setDisabled(true);
    }
  }, [subscriptionOverviewData]);

  useEffect(() => {
    if (waitingForConfig && summary && payload) {
      setWaitingForConfig(false);
    }
  }, [summary, payload, waitingForConfig]);

  return (
    <>
      <Helmet>
        <title>{t('titles.create-account')}</title>
      </Helmet>
      <CommonLayout>
        <StyledPurchasePlanContainer>
          <Breadcrumb />
          {!disabled && !waitingForConfig && (
            <form onSubmit={handleSubmit}>
              <StyledPurchasePlanContentContainer row>
                <StyledFormContainer withoutMargin>
                  {Chargebee?.inited && (
                    <CheckoutForm
                      formik={formik}
                      handleDropdownChange={handleDropdownChange}
                      handleCardInputsChange={handleCardInputsChange}
                      handleCardInputsBlur={handleCardInputsBlur}
                      handleZipCodeBlur={handleZipCodeBlur}
                      ref={cardRef}
                    />
                  )}
                </StyledFormContainer>
                <StyledSummaryContainer>
                  <Summary
                    handleError={handleError}
                    promotionalButtonId="kpfApplyCoupon"
                    buttonText={checkoutText || t('common.purchase-now')}
                    buttonId={buttonIds.checkoutPurchaseNow}
                    summary={summary as ISummary}
                    payload={payload as PlanPayload}
                    onAmountChange={handleAmountChange}
                    isLoading={isSubmited || isLoading}
                    changeBillingAddressResponse={data}
                    disabled={isLoading}
                    apiError={apiError}
                    countryCode={values.country}
                    withoutDisabledStyles
                    isCheckout
                  />
                </StyledSummaryContainer>
              </StyledPurchasePlanContentContainer>
            </form>
          )}
        </StyledPurchasePlanContainer>
      </CommonLayout>
    </>
  );
});
